/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import { getReactHydrationStats } from '@jira/hydration-stats-reporting/src/ui/index.tsx';
import getMeta from '@jira/platform__utils-get-meta/src/utils/get-meta/index.tsx';
import { retrieveAndSetPageLoadTrace } from '@jira/platform__app-framework-retrieve-and-set-page-load-trace/src/index.tsx';
import { getJiraCustomData } from '@jira/platform__app-framework-ufo-jira-additional-payload/src/index.tsx';
import { configure as configureBundleEvalTimings } from '@atlaskit/react-ufo/bundle-eval-timing';
import {
	ModuleLoadingProfiler,
	type InteractionMetrics,
} from '@atlaskit/react-ufo/interaction-metrics';
import { init as platformInit } from '@atlaskit/react-ufo/interaction-metrics-init';
import { configure as configureResourceTimings } from '@atlaskit/react-ufo/resource-timing/config';
import { startResourceTimingBuffer } from '@atlaskit/react-ufo/resource-timing/utils';
import type {
	AssetsConfig,
	AssetsConfigAllAtlassianArgs,
	AssetsConfigPreloadedArgs,
} from '@atlaskit/react-ufo/common';
import { configure as configureSsrTiming } from '@atlaskit/react-ufo/ssr';
import { fg } from '@atlassian/jira-feature-gating';
import { GlobalReactLooselyLazyProfiler } from '@atlassian/react-loosely-lazy/profiler';
import { expVal } from '@atlassian/jira-feature-experiments';
import { jiraBundleEvalTimingsConfig } from './common/utils/jira-bundle-eval-config.tsx';
import { jiraResourceTimingsConfig } from './common/utils/jiraResourceTimingsConfig.tsx';
import { jiraSsrConfig } from './common/utils/jira-ssr-config.tsx';

type Capability = 'feature_flag_access' | 'react_profiler';
type InteractionKind = 'page_load' | 'transition' | 'press' | 'typing' | 'legacy' | 'hover';
type UFONameOverride = {
	readonly [ufoName: string]: {
		readonly [metricKey: string]: string;
	};
};
// Defensively typed, since this is directly user-editable
// and they could delete empty members
type UFOConfigFromFF = {
	readonly doNotAbortActivePressInteraction?: string[];
	readonly doNotAbortActivePressInteractionOnTransition?: string[];
	readonly awaitBM3TTI?: string[];
	readonly ufoNameOverrides?: UFONameOverride;
	readonly rates?: {
		readonly [key: string]: number;
	};
	readonly rules?: readonly {
		readonly test?: string;
		readonly rate?: number;
	}[];
	readonly killswitch?: readonly string[];
	readonly typingMethod?: string;
	readonly capability?: Record<Capability, number>;
	readonly kind?: Record<InteractionKind, number>;
	readonly autoGeneratedRate?: number;
	readonly removePageSegmentsUFOPrefixes?: boolean;
	readonly removeInteractionsUFOPrefixes?: boolean;
};

const FALLBACK_UFO_CONFIG = {
	autoGeneratedRate: 1000,
	enabled: true,
	removeUFOPrefixList: ['project-versions', 'filters-directory'],
	removePageSegmentsUFOPrefixes: true,
	doNotAbortActivePressInteractionOnTransition: [
		'jsm-create-cmp-request-type-from-template',
		'jsm-create-tmp-request-type-from-template',
	],
	killswitch: ['jira.fe.load.UNKNOWN'],
	awaitForSegments: {
		'global-issue.create-modal': ['issue-view-details'],
		'global-issue.create-modal-subsequent': ['issue-view-details'],
	},
	removeAllUFOPrefix: true,
	rates: {
		'issue-view-activity-button-worklog': 1,
		'issue-view-activity-button-history': 1,
		'issue-view-activity-button-comments': 1,
		'arj-toggle-view-mode': 1,
		unknown: 1000,
		'issue-view-activity-button-forge-app': 1,
		'view-mode': 1,
		'backlog.issue-view-opened': 5,
		'create-web-link-inline-field-type-tracing': 1000,
		'issue-view-activity-button-connect-app': 1,
		'single-line-text-field-type-tracing': 100,
		'issue-view-activity-button-all': 1,
		'backlog.card-hover': 1,
		'board.card-drag': 1,
		'child-issue-inline-create-type-tracing': 100,
		'show-insights-button': 100,
		'board.issue-view-opened': 5,
		'issue-view-activity-button-jsd-comments': 1,
		'backlog.card-list-toggle': 5,
		'issue-view-activity-button-approvals': 1,
		'work-item-activity-button-forge-app': 1,
		'work-item-activity-button-connect-app': 1,
	},
	kind: {
		generic: 0,
		hover: 0,
		legacy: 1,
		page_load: 1,
		press: 1,
		transition: 1,
		typing: 0,
	},
	awaitBM3TTI: [
		'jwm_project_pages',
		'software-plan-dependencies',
		'advanced-roadmaps.page-plan.dependencies-tab.load-time',
		'servicedesk-cmdb-object-type-configuration',
		'service-management.insight-cmdb-object-type-configuration',
		'project-components-view',
		'project-components',
		'project-settings-components',
		'people-index',
		'people-profiles',
		'rapidboard-report',
		'classic-report',
		'project-settings-servicedesk-ticket-type-fields',
		'project-settings.issue-type.jsm-cmp-customer-view',
		'project-settings.issue-type.jsm-cmp-agent-view',
		'archived-project-direct-link',
		'project-archived',
		'servicedesk-queues-new',
		'service-management.queues.new',
		'project-settings-software-boards-insights',
		'board-settings-apps.insights-settings-page.page.load',
		'admin-oauth-credentials',
		'admin.oauth-credentials',
		'polaris-ideas',
		'project-settings-software-boards-csm',
		'jira.fe.page-load.software.next-gen.boards.column-status-mapping.page.load',
		'project-settings-servicedesk-language',
		'service-management.languages',
		'project-settings-servicedesk-language-key',
		'service-management.translations',
		'marketplace-index',
		'embedded-marketplace',
		'project-settings-servicedesk-portal-settings',
		'service-management.portal-settings',
		'service-management.portal-settings.portal-groups',
		'project-settings-servicedesk-automation-create',
		'service-management.automation.create-rule',
		'servicedesk-customers-v2',
		'jsm-customers-component-load',
		'archived-project-direct-link',
		'project-archived',
		'project-settings-software-boards-csm',
		'software.next-gen.boards.column-status-mapping.page.load',
		'jira-connect',
		'knowledge-hub-category',
		'service-management.knowledge.category',
		'servicedesk-queues-landing',
		'servicedesk-practice-landing',
		'servicedesk-practice-custom-and-issue',
		'servicedesk-practice-custom-issue',
		'servicedesk-practice-custom-view',
		'servicedesk-queues-custom-and-issue',
		'servicedesk-queues-custom-issue',
		'servicedesk-queues-custom-view',
	],
	rules: [],
	capability: {
		feature_flag_access: 1,
		react_profiler: 1,
	},
	typingMethod: 'timeout',
	removeInteractionsUFOPrefixes: true,
	doNotAbortActivePressInteraction: [],
	ufoNameOverrides: {
		'global-issue-navigator': {
			'global-issue-navigator': 'global-issue-navigator-jfe',
			'new-issue-navigator': 'global-new-issue-navigator',
		},
		'global-issue-navigator-jfe': {
			'global-issue-navigator-jfe': 'global-issue-navigator-jfe',
			'new-issue-navigator': 'global-new-issue-navigator',
		},
		'global-settings-migrations-focus-page': {
			'admin.c2c-migrations.choose-destination': 'admin.c2c-migrations.choose-destination',
			'admin.c2c-migrations.choose-projects': 'admin.c2c-migrations.choose-projects',
			'admin.c2c-migrations.preflight-checks': 'admin.c2c-migrations.preflight-checks',
			'admin.c2c-migrations.review': 'admin.c2c-migrations.review',
		},
		'polaris-ideas': {
			'project-settings.details': 'project-settings-details-polaris-ideas',
		},
		'project-settings-servicedesk-portal-settings': {
			'service-management.portal-settings': 'service-management.portal-settings',
			'service-management.portal-settings.portal-groups':
				'service-management.portal-settings.portal-groups',
		},
		'project-settings-servicedesk-ticket-type-fields': {
			'project-settings.issue-type.jsm-cmp-agent-view':
				'project-settings.issue-type.jsm-cmp-agent-view',
			'project-settings.issue-type.jsm-cmp-customer-view':
				'project-settings.issue-type.jsm-cmp-customer-view',
		},
		'servicedesk-practice-custom-and-issue': {
			'service-management.queues.issue': 'servicedesk-practice-custom-issue',
			'service-management.queues.view': 'servicedesk-practice-custom-view',
		},
		'servicedesk-queues-custom-and-issue': {
			'service-management.queues.issue': 'servicedesk-queues-custom-issue',
			'service-management.queues.view': 'servicedesk-queues-custom-view',
		},
	},
	enableCompleteUsingDoubleRAF: true,
};
const STRINGIFIED_FALLBACK_UFO_CONFIG = JSON.stringify(FALLBACK_UFO_CONFIG);

const getUFOConfigFromStatsig = (): UFOConfigFromFF => {
	try {
		// eslint-disable-next-line jira/ff/inline-expVal-params
		return JSON.parse(expVal('ufo_config', 'value', STRINGIFIED_FALLBACK_UFO_CONFIG));
	} catch (error) {
		// oxlint-disable-next-line no-console
		console.error('Failed to get UFO config from Statsig dynamic config:', error);
		// TODO: AFO-2949: Change the fallback to be `{}` once we have a stable config in Statsig when deprecating the FF 'ufo-use-statsig-config-jira'
		return FALLBACK_UFO_CONFIG;
	}
};

function getAdditionalJiraFields(interaction: InteractionMetrics) {
	try {
		return { jira: getJiraCustomData(interaction) };
	} catch (error) {
		return {};
	}
}

function loadJiraAnalyticsWebClient() {
	return import(
		/* webpackChunkName: "AWC" */ '@jira/platform__observability-product-analytics-web-client-async'
	);
}

export default function init(): void {
	const ufoConfigFromFF = fg('ufo-use-statsig-config-jira')
		? getUFOConfigFromStatsig()
		: FALLBACK_UFO_CONFIG;

	retrieveAndSetPageLoadTrace(getMeta);

	configureResourceTimings(jiraResourceTimingsConfig);
	startResourceTimingBuffer();

	if (!fg('jira-disable-ufo-bundle-eval-timings')) {
		configureBundleEvalTimings(jiraBundleEvalTimingsConfig);
	}

	configureSsrTiming(jiraSsrConfig);

	GlobalReactLooselyLazyProfiler.current = ModuleLoadingProfiler;

	const perfPushExperienceRates = {
		'issue-view': 1,
		'issue-view-software': 1,
		'issue-view-serviceDesk': 1,
		...(fg('jira_issue_view_core_ufo_page_load') ? { 'issue-view-core': 1 } : {}),
		'issue-view-embed': 1,
		'new-issue-navigator': 1,
		'classic-board': 1,
		'classic-backlog': 1,
		'next-gen-board': 1,
		'software-plan-timeline': 1,
		'next-gen-roadmap': 1,
		'classic-roadmap': 1,
		'next-gen-backlog': 1,
		'jwm.list-view': 1,
		'jwm.summary-view': 1,
		dashboard: 1,
		'global-new-issue-navigator': 1,
		...(fg('nin_split_with_issue_view')
			? {
					'global-new-issue-navigator-with-issue-view': 1,
				}
			: {}),
		'global-issue-navigator-jfe': 1,
		'issue-navigator': 1,
		'jwm.jwm-board-base': 1,
		'jwm.jwm-board-with-filters': 1,
		'jwm.jwm-board-with-issue-view': 1,
		'jira-board': 1,
		'jira-board-with-issue-view': 1,
		'servicedesk-queues-landing': 1,
		'servicedesk-practice-landing': 1,
		'servicedesk-practice-custom-issue': 1,
		'servicedesk-practice-custom-view': 1,
		'servicedesk-queues-custom-issue': 1,
		'servicedesk-queues-custom-view': 1,
		...(fg('jira_list_nin_merge')
			? {
					'software-list-nin': 1,
					'classic-list-nin': 1,
					'jwm-list-nin': 1,
					'jsm-list-nin': 1,
					...(fg('nin_split_with_issue_view')
						? {
								'software-list-nin-with-issue-view': 1,
								'classic-list-nin-with-issue-view': 1,
								'jwm-list-nin-with-issue-view': 1,
								'jsm-list-nin-with-issue-view': 1,
							}
						: {}),
				}
			: {
					'software-issue-navigator': 1,
					'issue-navigator-classic': 1,
					'issue-navigator-core': 1,
					'servicedesk-issues': 1,
					...(fg('nin_split_with_issue_view')
						? {
								'software-issue-navigator-with-issue-view': 1,
								'issue-navigator-classic-with-issue-view': 1,
								'issue-navigator-core-with-issue-view': 1,
								'servicedesk-issues-with-issue-view': 1,
							}
						: {}),
				}),
		'assets-app-schemas-page': 1,
		'assets-schema': 1,
		'assets-object-view-page': 1,
		'work-item': 1,
		...(expVal('platform_avp_dual_create_dashboards', 'isEnabled', false)
			? { 'dashboard-v2': 1 }
			: {}),
	};

	const ssrWhitelist = [
		'global-new-issue-navigator',
		...(fg('nin_split_with_issue_view') ? ['global-new-issue-navigator-with-issue-view'] : []),
		'global-issue-navigator-jfe',
		'issue-view',
		'issue-view-software',
		'issue-view-serviceDesk',
		...(fg('jira_issue_view_core_ufo_page_load') ? ['issue-view-core'] : []),
		'issue-view-embed',
		'classic-board',
		'jira-board',
		'classic-backlog',
		'next-gen-backlog',
		...(fg('jira_list_nin_merge')
			? [
					'software-list-nin',
					'classic-list-nin',
					'jwm-list-nin',
					'jsm-list-nin',
					...(fg('nin_split_with_issue_view')
						? [
								'software-list-nin-with-issue-view',
								'classic-list-nin-with-issue-view',
								'jwm-list-nin-with-issue-view',
								'jsm-list-nin-with-issue-view',
							]
						: []),
				]
			: [
					'software-issue-navigator',
					'issue-navigator-classic',
					'issue-navigator-core',
					'servicedesk-issues',
					...(fg('nin_split_with_issue_view')
						? [
								'software-issue-navigator-with-issue-view',
								'issue-navigator-classic-with-issue-view',
								'issue-navigator-core-with-issue-view',
								'servicedesk-issues-with-issue-view',
							]
						: []),
				]),
		'work-item',
		...(expVal('platform_avp_dual_create_dashboards', 'isEnabled', false) ? ['dashboard-v2'] : []),
	];

	const doNotAbortActivePressInteractionsOnTransitionList = [
		'case-management-settings-create-case-note',
		'case-management-settings-duplicate-case-note',
	];

	const assetsConfig: AssetsConfig = {
		classification: {
			all: true,
			allAtlassian: ({ entry }: AssetsConfigAllAtlassianArgs) => {
				const isAtlassian =
					entry.name.includes('atl-paas.net/') ||
					(entry.name.includes('cloudfront') && entry.name.includes('batch.js'));
				return isAtlassian;
			},
			preloaded: ({ entry, SSRDoneTime }: AssetsConfigPreloadedArgs) => {
				return Boolean(SSRDoneTime && entry.startTime <= SSRDoneTime);
			},
		},
	};

	platformInit(loadJiraAnalyticsWebClient(), {
		...ufoConfigFromFF,
		...(fg('jsm-case-management-settings')
			? {
					doNotAbortActivePressInteractionOnTransition: [
						...(ufoConfigFromFF.doNotAbortActivePressInteractionOnTransition ?? []),
						...doNotAbortActivePressInteractionsOnTransitionList,
					],
				}
			: {}),
		enableSegmentHighlighting: false,
		enableAdditionalPerformanceMarks: false,
		product: 'jira',
		region: getMeta('ajs-region') || 'unknown',
		...(fg('ufo-emit-is-sandbox-meta') ? { isSandbox: getMeta('ajs-isSandbox') === 'true' } : {}),
		additionalPayloadData: getAdditionalJiraFields,
		postInteractionLog: {
			enabled: true,
			rates: perfPushExperienceRates,
		},
		experimentalInteractionMetrics: {
			enabled: false,
			rates: perfPushExperienceRates,
		},
		manuallyTrackReactProfilerMounts: true,
		vc: {
			enabled: true,
			heatmapSize: 200,
			oldDomUpdates: false,
			devToolsEnabled: fg('uip_vc_devtools'),
			selectorConfig: {
				id: true,
				role: false,
				className: true,
				testId: false, // Cannot be enabled due to some testid attributes containing UGC. Use data-vc instead.
			},
			ssrWhitelist,
			ssrEnablePageLayoutPlaceholder: true,
			stopVCAtInteractionFinish: false,
			includeSSRInV3: true,
			includeSSRRatio: true,
			enabledVCRevisions: {
				all: fg('ufo_disable_ttvc_v4') ? [] : ['fy26.04'],
			},
			trackLayoutShiftOffenders: true,
		},
		enableBetterPageVisibilityApi: true,
		assetsConfig,
		getReactHydrationStats,
	});
}
